/*
Origin Core 1.0

called from /snippets/predictive-search.liquid

DEVELOPER NOTE
--------------------
any changes to this file should be noted here, and also noted in the following wiki page:
https://unifiedco.atlassian.net/wiki/spaces/ORIGIN/pages/1413414932/Core+File+Changes+1.0

SUMMARY
--------------------
This script defines a custom web component <predictive-search-component> that manages predictive search functionality for Shopify.
It handles user input, fetches and displays search results via AJAX, supports keyboard navigation, and updates the UI with
search results or the empty resting state. It handles dialog interactions and provides accessibility features.
It is registered as a custom element if not already defined.

*/

import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement} [scrollArea] - The wrapper that carries the scroll fade.
 * @property {HTMLElement} [body] - The scrolling results area.
 * @property {HTMLElement} [footer] - The view-all footer bar.
 * @property {HTMLAnchorElement} [viewAllButton] - The view-all link in the footer.
 * @property {HTMLElement} [viewAllLabel] - The label inside the view-all link.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      this.style.setProperty('--predictive-search-results-height', `${entry.contentRect.height}px`);
    }

    this.#updateScrollAffordance();
  });

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    this.#updateViewAll(this.refs.searchInput.value);

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      // Until the dialog opens the panel is display:none, so nothing can be measured —
      // the scroll fade has to be worked out once it is actually on screen.
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    } else {
      document.addEventListener(ThemeEvents.megaMenuHover, this.#blurSearch, { signal });
    }

    // `scroll` doesn't bubble, so it can't go through the declarative on: attributes.
    this.refs.body?.addEventListener('scroll', this.#updateScrollAffordance, { signal, passive: true });
    this.#updateScrollAffordance();

    const results = this.refs.predictiveSearchResults.firstElementChild;

    if (results) {
      this.#resizeObserver.observe(results);
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  /**
   * Handles the dialog open event.
   */
  #handleDialogOpen = () => {
    this.#updateScrollAffordance();
  };

  expandSearch = () => {
    // Add the expanded class to the search component
    this.classList.add('predictive-search--expanded');
    if (this.dataset.activeColorScheme) {
      const target = this.dialog ?? this;
      target.classList.add(`color-${this.dataset.activeColorScheme}`);
    }
  };

  /**
   * Every navigable result, in document order — suggestions first, then products.
   *
   * UNFD - this used to look for `.predictive-search-results__wrapper-*` containers,
   * which the results markup does not render, so arrow-key navigation never found
   * anything. Reading the refs the markup actually sets keeps the two in step.
   */
  get #allResultsItems() {
    const allItems = Array.from(this.querySelectorAll('[ref="resultsItems[]"]')).filter(
      (item) => item instanceof HTMLElement
    );

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.dataset.highlighted === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.dataset.highlighted = 'true';

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        delete item.dataset.highlighted;
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    // Arrow keys move through results; Tab deliberately falls through to the browser so
    // the clear, close and view-all controls stay reachable (WCAG 2.1.2, no keyboard trap).
    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter':
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
    }
  };

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#setBusy(true);
    this.#updateViewAll(searchTerm);
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Scrolls a freshly rendered result set back to the top.
   *
   * UNFD - this used to reset `.predictive-search-results__inner` and
   * `.predictive-search-form__content`, neither of which is a scroll container, so the
   * panel kept its old scroll offset and new results appeared already scrolled past.
   * The scrolling element is `.predictive-search__body` (ref: body).
   */
  #resetScrollPositions() {
    const { body } = this.refs;

    if (body) body.scrollTop = 0;

    // Measured synchronously rather than in requestAnimationFrame: the morph has already
    // landed by the time this runs, and rAF is throttled in a backgrounded tab, which
    // would leave the scroll fade stuck in whatever state it had before.
    this.#updateScrollAffordance();
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    // UNFD - Include variant SKU in product search (defaults are title, product_type, variants.title, vendor)
    url.searchParams.set('resources[options][fields]', 'author,body,title,product_type,variants.title,vendor,variants.sku');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#setBusy(false);
        this.#setExpanded(true);
        this.#updateViewAll(searchTerm);
        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        this.#setBusy(false);
        throw error;
      });
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  /**
   * Fades the last visible row while there is more of the panel to scroll to, so a card
   * clipped by the panel edge reads as scrollable rather than as a broken layout.
   */
  #updateScrollAffordance = () => {
    const { body, scrollArea } = this.refs;

    if (!body || !scrollArea) return;

    const overflows = body.scrollHeight - body.clientHeight > 2;
    const atEnd = body.scrollTop + body.clientHeight >= body.scrollHeight - 2;

    scrollArea.classList.toggle('is-scrollable', overflows && !atEnd);
  };

  /**
   * Toggles the loading treatment (progress bar + dimmed results).
   * @param {boolean} isBusy - Whether a request is in flight.
   */
  #setBusy(isBusy) {
    this.setAttribute('aria-busy', String(isBusy));
  }

  /**
   * Keeps the combobox's expanded state in sync with whether results are showing.
   * @param {boolean} isExpanded - Whether results are showing.
   */
  #setExpanded(isExpanded) {
    this.refs.searchInput.setAttribute('aria-expanded', String(isExpanded));
  }

  /**
   * Shows the 'View all results for "term"' footer and points it at the search page.
   * Predictive search returns a capped subset, so the label deliberately never
   * quotes a result count — it would understate the real total.
   * @param {string} searchTerm - The current search term.
   */
  #updateViewAll(searchTerm) {
    const { footer, viewAllButton, viewAllLabel } = this.refs;

    if (!footer || !viewAllButton) return;

    const term = searchTerm.trim();

    footer.hidden = term.length === 0;

    if (!term.length) return;

    const searchUrl = new URL(Theme.routes.search_url, location.origin);
    searchUrl.searchParams.set('q', term);
    viewAllButton.href = searchUrl.toString();

    const template = viewAllButton.dataset.labelTemplate;

    if (template && viewAllLabel) {
      viewAllLabel.textContent = template.replace('[[terms]]', term);
    }
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();
    this.#setBusy(false);
    this.#setExpanded(false);
    this.#updateViewAll('');

    const abortController = this.#createAbortController();
    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };

  /**
   * Closes the predictive search.
   */
  #blurSearch = () => {
    this.refs.searchInput.blur();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
